import React from 'react'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

type ButtonVariant = 'primary' | 'outline' | 'success' | 'warning' | 'danger' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  loading?: boolean
  block?: boolean
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-800 active:scale-95',
  outline: 'border-2 border-primary text-primary hover:bg-primary-50 active:scale-95',
  success: 'bg-success text-white hover:bg-green-600 active:scale-95',
  warning: 'bg-warning text-white hover:bg-orange-600 active:scale-95',
  danger: 'bg-error text-white hover:bg-red-600 active:scale-95',
  ghost: 'text-text-secondary hover:bg-gray-100 active:scale-95',
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'text-xs py-2 px-4',
  md: 'text-sm py-3 px-6',
  lg: 'text-base py-4 px-8',
}

export default function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  loading = false,
  block = false,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-semibold rounded-btn transition-all duration-200',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
        variantStyles[variant],
        sizeStyles[size],
        block && 'w-full',
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        leftIcon && <span className="w-4 h-4">{leftIcon}</span>
      )}
      {children}
      {!loading && rightIcon && <span className="w-4 h-4">{rightIcon}</span>}
    </button>
  )
}
